import { createContext, useContext, useReducer, useEffect, useCallback, useRef, useState, type ReactNode } from 'react';
import type { NormalizedItem, ModalState, PermissionSet } from './types';
import { getPermissions } from './types';
import { useAppDispatch } from '@/store/hooks';
import { updateCachedItem, removeCachedItems } from '@/store/itemsSlice';

// ── State ───────────────────────────────────────────────────────────

interface ItemListState {
  selectedItems: Map<string, NormalizedItem>;
  modal: ModalState | null;
  activeDropdownId: string | null;
  renamingItemId: string | null;
}

const initialState: ItemListState = {
  selectedItems: new Map(),
  modal: null,
  activeDropdownId: null,
  renamingItemId: null,
};

// ── Actions ─────────────────────────────────────────────────────────

type Action =
  | { type: 'SELECT_ITEM'; item: NormalizedItem }
  | { type: 'DESELECT_ITEM'; id: string }
  | { type: 'SELECT_ALL'; items: NormalizedItem[] }
  | { type: 'DESELECT_ALL' }
  | { type: 'OPEN_MODAL'; modal: ModalState }
  | { type: 'CLOSE_MODAL' }
  | { type: 'SET_DROPDOWN'; id: string | null }
  | { type: 'SET_RENAMING'; id: string | null }
  | { type: 'RESET_SELECTION' };

function reducer(state: ItemListState, action: Action): ItemListState {
  switch (action.type) {
    case 'SELECT_ITEM': {
      const next = new Map(state.selectedItems);
      next.set(action.item.id, action.item);
      return { ...state, selectedItems: next };
    }
    case 'DESELECT_ITEM': {
      const next = new Map(state.selectedItems);
      next.delete(action.id);
      return { ...state, selectedItems: next };
    }
    case 'SELECT_ALL': {
      const next = new Map<string, NormalizedItem>();
      action.items.forEach((item) => next.set(item.id, item));
      return { ...state, selectedItems: next };
    }
    case 'DESELECT_ALL':
    case 'RESET_SELECTION':
      return { ...state, selectedItems: new Map() };
    case 'OPEN_MODAL':
      return { ...state, modal: action.modal };
    case 'CLOSE_MODAL':
      return { ...state, modal: null };
    case 'SET_DROPDOWN':
      return { ...state, activeDropdownId: action.id };
    case 'SET_RENAMING':
      return { ...state, renamingItemId: action.id };
    default:
      return state;
  }
}

// ── Context ─────────────────────────────────────────────────────────

export interface ItemListContextValue {
  // State
  selectedItems: Map<string, NormalizedItem>;
  selectedItemsArray: NormalizedItem[];
  modal: ModalState | null;
  activeDropdownId: string | null;
  renamingItemId: string | null;
  permissions: PermissionSet;
  items: NormalizedItem[];
  // Selection actions
  selectItem: (item: NormalizedItem) => void;
  deselectItem: (id: string) => void;
  toggleItem: (item: NormalizedItem) => void;
  selectAll: () => void;
  deselectAll: () => void;
  isSelected: (id: string) => boolean;
  // UI state
  openModal: (type: ModalState['type'], params?: unknown) => void;
  closeModal: () => void;
  setDropdown: (id: string | null) => void;
  setRenaming: (id: string | null) => void;
  // Optimistic item mutations
  updateItem: (id: string, changes: Partial<NormalizedItem>) => void;
  removeItems: (ids: string[]) => void;
  // Sort state (so resource links can carry sort params)
  sortCriteria: number;
  sortAscending: boolean;
  // Callbacks from parent
  onRefresh: () => void;
  updateIsQuerying: (querying: boolean) => void;
}

const ItemListContext = createContext<ItemListContextValue | null>(null);

export function useItemListContext(): ItemListContextValue {
  const ctx = useContext(ItemListContext);
  if (!ctx) throw new Error('useItemListContext must be used within ItemListProvider');
  return ctx;
}

// ── Provider ────────────────────────────────────────────────────────

interface ItemListProviderProps {
  items: NormalizedItem[];
  userRole?: string | null;
  useCase?: string;
  parentId?: string;
  sortCriteria?: number;
  sortAscending?: boolean;
  onRefresh: () => void;
  updateIsQuerying: (querying: boolean) => void;
  children: ReactNode;
}

export function ItemListProvider({
  items: propItems,
  userRole,
  useCase,
  parentId,
  sortCriteria = 0,
  sortAscending = true,
  onRefresh,
  updateIsQuerying,
  children,
}: ItemListProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const reduxDispatch = useAppDispatch();
  const [localItems, setLocalItems] = useState(propItems);
  const permissions = getPermissions(userRole, useCase);
  const prevItemsRef = useRef(propItems);

  // Sync local items from props when parent updates (e.g., route change, full refresh)
  useEffect(() => {
    if (prevItemsRef.current !== propItems) {
      setLocalItems(propItems);
      dispatch({ type: 'RESET_SELECTION' });
      prevItemsRef.current = propItems;
    }
  }, [propItems]);

  // ── Selection ───────────────────────────────────────────────────

  const selectItem = useCallback((item: NormalizedItem) => dispatch({ type: 'SELECT_ITEM', item }), []);
  const deselectItem = useCallback((id: string) => dispatch({ type: 'DESELECT_ITEM', id }), []);
  const toggleItem = useCallback((item: NormalizedItem) => {
    if (state.selectedItems.has(item.id)) {
      dispatch({ type: 'DESELECT_ITEM', id: item.id });
    } else {
      dispatch({ type: 'SELECT_ITEM', item });
    }
  }, [state.selectedItems]);
  const selectAll = useCallback(() => dispatch({ type: 'SELECT_ALL', items: localItems }), [localItems]);
  const deselectAll = useCallback(() => dispatch({ type: 'DESELECT_ALL' }), []);
  const isSelected = useCallback((id: string) => state.selectedItems.has(id), [state.selectedItems]);

  // ── UI state ────────────────────────────────────────────────────

  const openModal = useCallback((type: ModalState['type'], params?: unknown) => {
    dispatch({ type: 'OPEN_MODAL', modal: { type, params } });
  }, []);
  const closeModal = useCallback(() => dispatch({ type: 'CLOSE_MODAL' }), []);
  const setDropdown = useCallback((id: string | null) => dispatch({ type: 'SET_DROPDOWN', id }), []);
  const setRenaming = useCallback((id: string | null) => dispatch({ type: 'SET_RENAMING', id }), []);

  // ── Optimistic mutations ────────────────────────────────────────

  const updateItem = useCallback((id: string, changes: Partial<NormalizedItem>) => {
    setLocalItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    );
    // Sync to global items cache
    if (parentId) {
      reduxDispatch(updateCachedItem({ parentId, itemId: id, changes }));
    }
  }, [parentId, reduxDispatch]);

  const removeItems = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    setLocalItems((prev) => prev.filter((item) => !idSet.has(item.id)));
    // Also remove from selection
    ids.forEach((id) => dispatch({ type: 'DESELECT_ITEM', id }));
    // Sync to global items cache
    if (parentId) {
      reduxDispatch(removeCachedItems({ parentId, itemIds: ids }));
    }
  }, [parentId, reduxDispatch]);

  const selectedItemsArray = Array.from(state.selectedItems.values());

  const value: ItemListContextValue = {
    selectedItems: state.selectedItems,
    selectedItemsArray,
    modal: state.modal,
    activeDropdownId: state.activeDropdownId,
    renamingItemId: state.renamingItemId,
    permissions,
    items: localItems,
    selectItem,
    deselectItem,
    toggleItem,
    selectAll,
    deselectAll,
    isSelected,
    openModal,
    closeModal,
    setDropdown,
    setRenaming,
    updateItem,
    removeItems,
    sortCriteria,
    sortAscending,
    onRefresh,
    updateIsQuerying,
  };

  return (
    <ItemListContext.Provider value={value}>
      {children}
    </ItemListContext.Provider>
  );
}
